import React, { useState, useEffect } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler } from 'chart.js';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import './Dashboard.css';
import { getAuthHeader } from '../../utils/auth';

// --- IMPORT THE NEW COMPONENT HERE ---
import ResumenHoteles from './ResumenHoteles';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler);

// === API CONFIGURATION (PRODUCTION READY) ===
const getApiUrl = () => {
  // Check for environment variable
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  // Default to relative path for production (same domain)
  return ''; 
};

const API_URL = getApiUrl();

// Helper to format numbers
const renderNumber = (value, decimals = 2, prefix = '', suffix = '') => {
    const num = parseFloat(value);
    if (isNaN(num)) return `N/A`;
    const formatted = num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return `${prefix}${formatted}${suffix}`;
};

// Component for the KPI Gauge Chart
function KpiGauge({ value, label }) {
    const data = {
        datasets: [{
            data: [value, 100 - value],
            backgroundColor: ['#588395', '#E0E0E0'],
            borderColor: ['#588395', '#E0E0E0'],
            circumference: 180,
            rotation: 270,
            cutout: '70%',
            borderWidth: 0,
            borderRadius: 5,
        }]
    };
    const options = { responsive: true, maintainAspectRatio: true, legend: { display: false }, tooltip: { enabled: false } };
    
    return (
        <div className="gauge-container">
            <Doughnut data={data} options={options} />
            <div className="gauge-label">
                <div className="gauge-value">{renderNumber(value, 0, '', '%')}</div>
                <div className="gauge-text">{label}</div>
            </div>
        </div>
    );
}

// Component for displaying KPI change (e.g., +5% ▲, -3% ▼)
function KpiChange({ change }) {
    if (change === null || isNaN(change)) {
        return <div className="kpi-change neutral">N/A</div>;
    }
    const isPositive = change > 0;
    const arrow = isPositive ? '▲' : '▼';
    const sign = isPositive ? '+' : '';
    const className = isPositive ? 'kpi-change positive' : 'kpi-change negative';

    return (
        <div className={className}>
            {sign}{renderNumber(change, 1, '', '%')} {arrow}
        </div>
    );
}

// Component for a single Index Metric (e.g., MPI, ARI, RevPAR, RGI)
function IndexMetric({ value, label, change, prefix = '', decimals = 1 }) {
    return (
        <div className="index-metric-item">
            <div className="index-label">{label}</div>
            <div className="index-value">{renderNumber(value, decimals, prefix)}</div>
            <KpiChange change={change} />
        </div>
    );
}

function CompSetRoomsPieChart({ compsetRoomsBreakdown }) {
    // Add a console log here while testing; open your browser 'Inspect' -> 'Console'
    // to see exactly what the component is receiving.
    console.log("Chart received data:", compsetRoomsBreakdown);

    if (!compsetRoomsBreakdown || compsetRoomsBreakdown.length === 0) {
        return (
            <div className="comp-set-pie-chart-placeholder">
                <p>No hay datos de CompSet para este hotel.</p>
                <span style={{fontSize: '10px', color: '#ccc'}}>ID: {compsetRoomsBreakdown ? 'Empty Array' : 'Null/Undefined'}</span>
            </div>
        );
    }
    // ... rest of your chart code
}


// Component for each hotel card
function HotelCard({ initialHotelData }) {
    const hotel = initialHotelData;

    if (!hotel || !hotel.kpis || !hotel.hotel_id) {
        return <div className="hotel-card">Cargando datos del hotel...</div>;
    }

    // --- NEW: Function to format the date title as requested ---
    const formatSubtitle = () => {
        // This check prevents a crash if the date properties are missing
        if (!hotel.date_this_year || !hotel.date_last_year) {
            // Fallback to the simple date if new properties aren't available
            return hotel.date ? hotel.date.split('-').reverse().join('/') : 'Fecha no disponible';
        }
        try {
            const dateThisYear = parseISO(hotel.date_this_year);
            const dateLastYear = parseISO(hotel.date_last_year);

            const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

            const formattedThisYear = capitalize(format(dateThisYear, "EEEE, d 'de' MMMM, yyyy", { locale: es }));
            const formattedLastYear = capitalize(format(dateLastYear, "EEEE, d 'de' MMMM, yyyy", { locale: es }));
            
            return `${formattedThisYear} (comparado con ${formattedLastYear})`;
        } catch (e) {
            console.error("Error formatting date:", e);
            return hotel.date_this_year; // Fallback
        }
    };

    // These constants must be defined inside the component function, before its return statement.
    const occupancyData = {
        labels: hotel.weekly_occupancy?.labels || [],
        datasets: [
            { label: 'Esta semana', data: hotel.weekly_occupancy?.this_week || [], borderColor: 'rgb(54, 162, 235)', backgroundColor: 'rgba(54, 162, 235, 0.2)', fill: true, tension: 0.4, },
            { label: 'El año pasado', data: hotel.weekly_occupancy?.last_year || [], borderColor: 'rgb(75, 192, 192)', backgroundColor: 'rgba(75, 192, 192, 0.2)', fill: true, tension: 0.4, }
        ]
    };

    const occupancyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += Math.round(context.parsed.y) + '%'; // Rounds to nearest whole number
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 115,
                ticks: {
                    callback: (value) => `${value}%`
                }
            },
            x: { // X-axis configuration for labels
                display: true, // Make sure the X-axis is displayed
                ticks: {
                    autoSkip: false, // Prevents Chart.js from hiding labels
                    maxRotation: 45, // Rotate labels up to 45 degrees
                    minRotation: 45, // Keep them rotated consistently
                    color: '#343a40', // Set a visible color (dark gray)
                    font: {
                        size: 11, // Set a readable font size
                        weight: 'bold' // Make them bold
                    },
                    callback: function(value, index, ticks) {
                        // 'value' here will be the string like "Mon Jun 09" from your backend labels array.
                        return value; // Explicitly return the full label string
                    }
                },
                grid: {
                    display: false // Hide vertical grid lines
                }
            }
        }
    };

    const finalImageUrl = hotel.image_url ? hotel.image_url : `https://placehold.co/200x150/A0A0A0/FFFFFF?text=${encodeURIComponent(hotel.name)}`;

    // --- Start of HotelCard's JSX return statement ---
    return (
     
        <div className="hotel-card">
            <div className="hotel-card-header">
                <img src={finalImageUrl} alt={hotel.name} className="hotel-image" onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/200x150/CCCCCC/666666?text=Image+Missing" }} />
                <div className="hotel-title">
                    <h3>{hotel.name}</h3>
                    {/* --- FIX: Use the new formatting function --- */}
                    <p>{formatSubtitle()}</p>
                </div>
            </div>
            {/* Main content area below header - now a 2-column grid */}
            <div className="hotel-card-body hotel-card-grid">
                {/* Left Column: CompSet display, KPI Section, and Chart Section stacked */}
                <div className="metrics-and-chart-column">
                    {/* Displaying the name of the EFFECTIVE CompSet (which is the default from backend) */}
                    <div className="compset-display-card">
                        <span className="compset-label">CompSet (Default):</span>
                        <span className="compset-name">
                            {hotel.effective_compset_name || `ID: ${hotel.effective_compset_id}`}
                        </span>
                    </div>

                    <div className="kpi-section">
                        <div className="kpi-group">
                            <KpiGauge value={hotel.kpis?.occupancy?.value} label="Ocupación" />
                            <KpiChange change={hotel.kpis?.occupancy?.change} />
                        </div>
                        <div className="kpi-group">
                            <div className="kpi-big-number">
                                <div className="kpi-value">{renderNumber(hotel.kpis?.adr?.value, 2, '$')}</div>
                                <div className="kpi-label">ADR</div>
                            </div>
                            <KpiChange change={hotel.kpis?.adr?.change} />
                        </div>
                    </div>
                    <div className="chart-section">
                        <h4>Ocupación Semanal</h4>
                        {/* console.log("DEBUG Chart Options (from HotelCard):", occupancyOptions) */} {/* Keep this commented or remove it */}
                        <Line options={occupancyOptions} data={occupancyData} />
                    </div>
                </div>

                {/* Right Column: Indices Section and CompSet Pie Chart stacked */}
                <div className="indices-and-pie-column">
                    <div className="indices-column">
                        <h4>Índices (Comp Set)</h4>
                        <div className="indices-grid">
                            <IndexMetric label="MPI" value={hotel.indices?.mpi?.value} change={hotel.indices?.mpi?.change} />
                            <IndexMetric label="ARI" value={hotel.indices?.ari?.value} change={hotel.indices?.ari?.change} />
                            <IndexMetric label="RevPAR" value={hotel.indices?.revpar?.value} change={hotel.indices?.revpar?.change} prefix="$" decimals={2} />
                            <IndexMetric label="RGI" value={hotel.indices?.rgi?.value} change={hotel.indices?.rgi?.change} />
                        </div>
                    </div>
                    <div className="comp-set-chart-column">
    {/* We add a fallback check here. 
      If 'compset_rooms_breakdown_for_card' is empty, 
      try checking if the API sent it under a different common name like 'compset_data' 
    */}
    <CompSetRoomsPieChart 
        compsetRoomsBreakdown={
            hotel.compset_rooms_breakdown_for_card || 
            hotel.compset_data || 
            [] 
        } 
    />
</div>
                </div>
            </div>
        </div>
    );
}
// --- End of HotelCard component ---

// Main Dashboard Component (The rest of Dashboard.js remains unchanged)
function Dashboard() {
    const [dashboardData, setDashboardData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                console.log("Dashboard: Initial fetch of all Propio hotels data...");
                // --- FIX: Use API_URL constant instead of hardcoded localhost ---
                const response = await fetch(`${API_URL}/api.php?path=dashboard`, { headers: getAuthHeader() });

                if (!response.ok) {
                    const errorText = await response.text();
                    let errorMessage = `HTTP error! Status: ${response.status}`;
                    try {
                        const errorBody = JSON.parse(errorText);
                        errorMessage = errorBody.message || errorMessage;
                    } catch (e) {
                        errorMessage = `Non-JSON response received: ${errorText.substring(0, 100)}...`;
                    }
                    throw new Error(errorMessage);
                }
                const data = await response.json();
                console.log("Dashboard: Received initial dashboard data:", data.dashboardData);

                if (data.success) {
                    setDashboardData(data.dashboardData);
                } else {
                    throw new Error(data.message || 'API returned an error.');
                }
            } catch (err) {
                console.error("Dashboard: Error fetching initial dashboard data:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    /* --- MOBILE-FRIENDLY STATUS RENDERING --- */
  if (loading || error) {
    return (
      <div className="dashboard-container">
        <h1 className="page-title">Dashboard</h1>
        <div className="dashboard-status-wrapper">
          {loading ? (
            <div className="status-message">
              <p>Cargando Dashboard...</p>
            </div>
          ) : (
            <div className="status-message error-state">
              <h3>⚠️ Error de Conexión</h3>
              <p>{error}</p>
              <p className="error-hint">Tu sesión puede haber expirado o hay un problema con la base de datos.</p>
              <button 
                className="emergency-logout-btn"
                onClick={() => {
                  localStorage.clear();
                  window.location.href = '/login';
                }}
              >
                Cerrar Sesión y Reintentar
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
    return (
        <div className="dashboard-container">
            <h1 className="page-title">Dashboard</h1>
            
            {/* --- INTEGRATED COMPONENT: RESUMEN DE HOTELES --- */}
            <ResumenHoteles />
            
            <div className="dashboard-grid">
                {dashboardData.map(hotel => {
                    return (
                        <HotelCard
                            key={hotel.hotel_id}
                            initialHotelData={hotel}
                        />
                    );
                })}
            </div>
        </div>
    );
}

export default Dashboard;